// PreviewTester/utils/parseTemplateForEdit.js

export const parseTemplateForEdit = (templateString, parsedSample, isCase2 = false, linesToUse = null) => {
  console.log('==================================================');
  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 Called');
  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 isCase2:', isCase2);
  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 templateString length:', templateString?.length || 0);
  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 linesToUse:', linesToUse ? `Array(${linesToUse.length})` : 'null');

  if (linesToUse) {
    console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 linesToUse details:');
    linesToUse.forEach((line, idx) => {
      console.log(`  [${idx}] id: ${line.id}, type: ${line.type}, content: "${line.content?.substring(0, 30)}${line.content?.length > 30 ? '...' : ''}", isCustom: ${line.isCustom}, isButtonGroup: ${line.isButtonGroup}, isButton: ${line.isButton}`);
    });
  }

  if (!templateString) {
    console.log('[parseTemplateForEditNeverOnFirstLoad] ⚠️ templateString is empty, returning []');
    return [];
  }

  const lines = templateString.split('\n');
  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 template lines count:', lines.length);

  const result = [];
  const loopStack = [];
  let currentDepth = 0;
  let inLoop = false;

  // ============================================================
  // ✅ Helper: Get button text safely (handles both string and object)
  // ============================================================
  const getButtonTextSafe = (btn) => {
    if (!btn) return '';
    if (typeof btn === 'string') return btn;
    if (typeof btn === 'object' && btn !== null) {
      return btn.text || btn.label || btn.name || btn.value || String(btn);
    }
    return String(btn);
  };

  // ============================================================
  // Store ALL lines by content
  // ============================================================
  const existingLinesByContent = new Map();

  if (linesToUse) {
    linesToUse.forEach((line, idx) => {
      const trimmed = (line.content || '').trim ? (line.content || '').trim() : String(line.content || '').trim();
      if (trimmed) {
        if (!existingLinesByContent.has(trimmed)) {
          existingLinesByContent.set(trimmed, []);
        }
        existingLinesByContent.get(trimmed).push({ line, index: idx });
      }
    });
  }

  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 existingLinesByContent entries:');
  for (const [content, entries] of existingLinesByContent) {
    console.log(`  "${content}" -> ${entries.length} entries:`, entries.map(e => `${e.line.type}(${e.index})`).join(', '));
  }

  // ============================================================
  // Helper: Find existing line by content - INDEX FIRST
  // ============================================================
  const findExistingLine = (lineContent, currentIndex) => {
    const trimmed = lineContent.trim ? lineContent.trim() : String(lineContent).trim();
    if (!trimmed) return null;

    const entries = existingLinesByContent.get(trimmed);
    if (!entries || entries.length === 0) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ⚠️ No entries found for: "${trimmed}"`);
      return null;
    }

    if (entries.length === 1) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Found single entry for "${trimmed}" -> type: ${entries[0].line.type}`);
      return entries[0].line;
    }

    console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔍 Multiple entries (${entries.length}) for "${trimmed}"`);

    // ✅ FIRST: Try to match by index (most accurate)
    const indexMatch = entries.find(e => e.index === currentIndex);
    if (indexMatch) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Found exact index match at ${currentIndex} -> type: ${indexMatch.line.type}`);
      return indexMatch.line;
    }

    // ✅ SECOND: Priority order for same content
    // Priority 1: Button (preserve user's button)
    const buttonEntry = entries.find(e => e.line.type === 'button' || e.line.isButton === true);
    if (buttonEntry) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Found button entry -> using it (priority 1)`);
      return buttonEntry.line;
    }

    // Priority 2: Button group
    const buttonGroupEntry = entries.find(e => e.line.type === 'button-group' || e.line.isButtonGroup === true);
    if (buttonGroupEntry) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Found button group entry -> using it (priority 2)`);
      return buttonGroupEntry.line;
    }

    // Priority 3: Custom message
    const customEntry = entries.find(e => e.line.type === 'custom' || e.line.isCustom === true);
    if (customEntry) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Found custom message entry -> using it (priority 3)`);
      return customEntry.line;
    }

    // Priority 4: Use the one closest to current index
    const closestEntry = entries.reduce((closest, current) => {
      const currentDist = Math.abs(current.index - currentIndex);
      const closestDist = Math.abs(closest.index - currentIndex);
      return currentDist < closestDist ? current : closest;
    });
    console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Using closest entry (index ${closestEntry.index}) -> type: ${closestEntry.line.type}`);
    return closestEntry.line;
  };

  // ============================================================
  // Helper: Extract variables from content
  // ============================================================
  const extractVariables = (content) => {
    const variables = [];
    const varRegex = /{{[^}]+}}/g;
    let match;
    while ((match = varRegex.exec(content)) !== null) {
      const varName = match[0].replace(/[{}]/g, '').trim();
      if (varName && !variables.includes(varName)) {
        variables.push(varName);
      }
    }
    return variables;
  };

  // ============================================================
  // ✅ FIX: Check if a line is a custom message - TYPE FIRST
  // ============================================================
  const isCustomMessage = (existingLine) => {
    if (!existingLine) return false;

    if (existingLine.type === 'button') {
      console.log('[parseTemplateForEditNeverOnFirstLoad] 🔍 isCustomMessage: type === "button" -> false (button)');
      return false;
    }

    if (existingLine.type === 'button-group') {
      console.log('[parseTemplateForEditNeverOnFirstLoad] 🔍 isCustomMessage: type === "button-group" -> false (button group)');
      return false;
    }

    if (existingLine.type === 'custom') {
      console.log('[parseTemplateForEditNeverOnFirstLoad] 🔍 isCustomMessage: type === "custom" -> true');
      return true;
    }
    if (existingLine.isCustom === true) {
      console.log('[parseTemplateForEditNeverOnFirstLoad] 🔍 isCustomMessage: isCustom === true -> true');
      return true;
    }
    if (existingLine.customId) {
      console.log('[parseTemplateForEditNeverOnFirstLoad] 🔍 isCustomMessage: has customId -> true');
      return true;
    }

    console.log('[parseTemplateForEditNeverOnFirstLoad] 🔍 isCustomMessage: false');
    return false;
  };

  // ============================================================
  // Helper: Check if a line is a button group - EDITOR VERSION
  // ============================================================
  const isButtonGroupLine = (trimmed, existingLine, currentIndex) => {
    if (!trimmed) return false;

    if (existingLine && existingLine.isButtonGroup === false) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔍 isButtonGroupLine: "${trimmed}" is explicitly NOT a button group -> false`);
      return false;
    }

    if (existingLine) {
      if (existingLine.type === 'button-group') {
        console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔍 isButtonGroupLine: "${trimmed}" has type === "button-group" -> true`);
        return true;
      }
      if (existingLine.isButtonGroup === true) {
        console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔍 isButtonGroupLine: "${trimmed}" has isButtonGroup === true -> true`);
        return true;
      }
    }

    if (isCustomMessage(existingLine)) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔍 isButtonGroupLine: "${trimmed}" is a custom message, NOT a button group`);
      return false;
    }

    if (trimmed.startsWith('[Button Group]')) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔍 isButtonGroupLine: "${trimmed}" matches [Button Group] pattern -> true`);
      return true;
    }

    const exactButtonGroupMatch = trimmed.match(/^Button Group\s*(\d*)$/);
    if (exactButtonGroupMatch) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔍 isButtonGroupLine: "${trimmed}" is exactly "Button Group" with optional number -> true`);
      return true;
    }

    console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔍 isButtonGroupLine: "${trimmed}" -> false`);
    return false;
  };

  // ============================================================
  // Helper: Check if a line is a button (single) - EDITOR VERSION
  // ============================================================
  const isButtonLine = (trimmed, existingLine, currentIndex) => {
    if (!trimmed) return false;

    if (existingLine && existingLine.isButton === false) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔍 isButtonLine: "${trimmed}" is explicitly NOT a button -> false`);
      return false;
    }

    if (existingLine && existingLine.isButton === true) {
      return true;
    }
    if (existingLine && existingLine.type === 'button') {
      return true;
    }

    if (isCustomMessage(existingLine) && !existingLine?.isButton) {
      return false;
    }

    return false;
  };

  // ============================================================
  // Helper: Check if a line is a template tag
  // ============================================================
  const isTemplateTag = (trimmed) => {
    return trimmed.startsWith('{%') || trimmed.startsWith('{#') || trimmed.startsWith('{% comment');
  };

  // ============================================================
  // Helper: Check if a line is a section header
  // ============================================================
  const isSectionHeader = (trimmed) => {
    return /^[A-Za-z\s]+:$/.test(trimmed);
  };

  // ============================================================
  // Helper: Check if a line is a key-value pair
  // ============================================================
  const isKeyValuePair = (trimmed) => {
    return /^([a-zA-Z_][a-zA-Z0-9_]*)\s*[-:=>]\s*(.+)$/.test(trimmed);
  };

  // ============================================================
  // MAIN PARSING LOOP
  // ============================================================
  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 Starting main parsing loop...');

  for (let i = 0; i < lines.length; i++) {
    const originalLine = lines[i];
    const trimmed = originalLine.trim ? originalLine.trim() : String(originalLine).trim();

    console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 --- Line ${i} ---`);
    console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 originalLine: "${originalLine}"`);
    console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 trimmed: "${trimmed}"`);

    const existingLine = findExistingLine(originalLine, i);

    console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 existingLine found:`, existingLine ? {
      id: existingLine.id,
      type: existingLine.type,
      isCustom: existingLine.isCustom,
      isButtonGroup: existingLine.isButtonGroup,
      isButton: existingLine.isButton,
      customId: existingLine.customId,
      content: existingLine.content?.substring(0, 30) + (existingLine.content?.length > 30 ? '...' : '')
    } : 'null');

    // ============================================================
    // STEP 1: Check for Button - HIGHEST PRIORITY
    // ============================================================
    if (isButtonLine(trimmed, existingLine)) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: "${trimmed}" is a BUTTON`);

      result.push({
        id: existingLine?.id || `button-${Date.now()}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'button',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: existingLine?.variables || extractVariables(originalLine),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: true,
        isButton: true,
        isButtonGroup: false,
        customId: existingLine?.customId || `button-${Date.now()}`,
        customMessage: existingLine?.customMessage || trimmed,
        buttonText: existingLine?.buttonText || trimmed,
        needsTransformationStep: true,
        _sourceType: 'button',
      });
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Added as BUTTON: "${trimmed}"`);
      continue;
    }

    // ============================================================
    // STEP 2: Check for Custom Message
    // ============================================================
    if (isCustomMessage(existingLine)) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Line ${i}: "${trimmed}" is a CUSTOM MESSAGE (preserving type)`);

      result.push({
        id: existingLine?.id || `custom-${Date.now()}-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'custom',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: existingLine?.variables || extractVariables(originalLine),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: true,
        customId: existingLine?.customId || `custom-${Date.now()}-${i}`,
        customMessage: existingLine?.customMessage || trimmed,
        isButton: false,
        isButtonGroup: false,
        buttons: null,
        needsTransformationStep: false,
        _sourceType: 'custom',
      });
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Added as CUSTOM: "${trimmed}"`);
      continue;
    }

    // ============================================================
    // STEP 3: Check for Button Group
    // ============================================================
    if (isButtonGroupLine(trimmed, existingLine)) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: "${trimmed}" is a BUTTON GROUP`);

      if (existingLine && existingLine.type === 'button-group') {
        console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Preserving existing button group with id: ${existingLine.id}`);
        result.push({
          ...existingLine,
          id: existingLine.id || `button-group-${Date.now()}`,
          content: originalLine,
          trimmed: trimmed,
          indent: originalLine.match(/^(\s*)/)?.[1] || '',
          depth: currentDepth,
          type: 'button-group',
          isButtonGroup: true,
          isCustom: false,
          buttons: existingLine.buttons && existingLine.buttons.length > 0
            ? existingLine.buttons.map((btn) => ({
                ...btn,
                text: getButtonTextSafe(btn),
              }))
            : [],
          _sourceType: 'button-group',
        });
        continue;
      }

      console.log(`[parseTemplateForEditNeverOnFirstLoad] 🔧 Creating new button group from content: "${trimmed}"`);
      const buttonGroupMatch = trimmed.match(/^\[Button Group\]\s*(.+)$/);
      let buttonNames = [];

      if (buttonGroupMatch) {
        buttonNames = buttonGroupMatch[1].split(',').map(b => b.trim()).filter(b => b);
      } else {
        const nameMatch = trimmed.match(/^Button Group\s*(.+)$/);
        if (nameMatch) {
          const name = nameMatch[1].trim();
          if (name && !isNaN(name)) {
            buttonNames = [`Button ${name}`];
          } else {
            buttonNames = name.split(',').map(b => b.trim()).filter(b => b);
            if (buttonNames.length === 0) {
              buttonNames = ['Button 1'];
            }
          }
        } else {
          buttonNames = ['Button 1'];
        }
      }

      const buttonObjects = buttonNames.map((text, idx) => ({
        id: `btn-${Date.now()}-${idx}`,
        text: text,
        index: idx,
      }));

      result.push({
        id: `button-group-${Date.now()}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'button-group',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButtonGroup: true,
        customId: null,
        customMessage: null,
        buttons: buttonObjects,
        needsTransformationStep: true,
        isButton: false,
        _sourceType: 'button-group',
      });
      console.log(`[parseTemplateForEditNeverOnFirstLoad] ✅ Added as BUTTON GROUP with ${buttonObjects.length} buttons`);
      continue;
    }

    // ============================================================
    // STEP 4: Handle template tags (for, if, endif, endfor, etc.)
    // ============================================================

    const forMatch = trimmed.match(/{%\s*for\s+([a-zA-Z_][a-zA-Z0-9_]*)\s+in\s+([^%]+)\s*%}/);
    if (forMatch) {
      const loopVar = forMatch[1];
      const loopArray = forMatch[2].trim();

      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: FOR LOOP START (${loopVar})`);

      loopStack.push({ varName: loopVar, depth: currentDepth });
      currentDepth++;
      inLoop = true;

      result.push({
        id: `for-start-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'for_loop_start',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: true,
        isLoopEnd: false,
        loopVariable: loopVar,
        loopArray: loopArray,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 1 && inLoop,
        isInIfBlock: false,
        depth: currentDepth - 1,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'loop_start',
      });
      continue;
    }

    const endForMatch = trimmed.match(/{%\s*endfor\s*%}/);
    if (endForMatch && loopStack.length > 0) {
      const loopInfo = loopStack.pop();
      currentDepth--;
      if (loopStack.length === 0) {
        inLoop = false;
      }

      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: FOR LOOP END`);

      result.push({
        id: `for-end-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'for_loop_end',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: true,
        loopVariable: loopInfo?.varName || null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'loop_end',
      });
      continue;
    }

    // ============================================================
    // STEP 5: Handle IF conditions
    // ============================================================
    const ifMatch = trimmed.match(/{%\s*if\s+([^%]+)\s*%}/);
    if (ifMatch) {
      const condition = ifMatch[1].trim();
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: IF START: ${condition}`);
      result.push({
        id: `if-start-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'if_start',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: condition,
        variables: extractVariables(condition),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: true,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'if_start',
      });
      continue;
    }

    const elifMatch = trimmed.match(/{%\s*elif\s+([^%]+)\s*%}/);
    if (elifMatch) {
      const condition = elifMatch[1].trim();
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: ELIF: ${condition}`);
      result.push({
        id: `elif-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'elif',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: condition,
        variables: extractVariables(condition),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: true,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'elif',
      });
      continue;
    }

    const elseMatch = trimmed.match(/{%\s*else\s*%}/);
    if (elseMatch) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: ELSE`);
      result.push({
        id: `else-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'else',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: true,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'else',
      });
      continue;
    }

    const endifMatch = trimmed.match(/{%\s*endif\s*%}/);
    if (endifMatch) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: ENDIF`);
      result.push({
        id: `endif-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'if_end',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'if_end',
      });
      continue;
    }

    // ============================================================
    // STEP 6: Handle comments and empty lines
    // ============================================================
    if (trimmed.startsWith('{#') || trimmed.startsWith('{% comment')) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: COMMENT`);
      result.push({
        id: `comment-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'comment',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'comment',
      });
      continue;
    }

    if (!trimmed) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: EMPTY`);
      result.push({
        id: `empty-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'empty',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'empty',
      });
      continue;
    }

    // ============================================================
    // STEP 7: Handle regular text content
    // ============================================================
    if (isSectionHeader(trimmed)) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: SECTION HEADER: "${trimmed}"`);
      result.push({
        id: `text-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'text',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: extractVariables(originalLine),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'text',
      });
      continue;
    }

    if (isKeyValuePair(trimmed)) {
      console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: KEY-VALUE: "${trimmed}"`);
      result.push({
        id: `text-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'text',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: extractVariables(originalLine),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'text',
      });
      continue;
    }

    console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄 Line ${i}: PLAIN TEXT: "${trimmed}"`);
    result.push({
      id: `text-${i}`,
      content: originalLine,
      trimmed: trimmed,
      type: 'text',
      indent: originalLine.match(/^(\s*)/)?.[1] || '',
      isLoopStart: false,
      isLoopEnd: false,
      loopVariable: null,
      loopArray: null,
      condition: null,
      variables: extractVariables(originalLine),
      isInForLoop: currentDepth > 0 && inLoop,
      isInIfBlock: false,
      depth: currentDepth,
      originalIndex: -1,
      isCustom: false,
      isButton: false,
      isButtonGroup: false,
      _sourceType: 'text',
    });
  }

  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 ===== FINAL PARSED LINES =====');
  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 Total lines:', result.length);
  result.forEach((line, idx) => {
    console.log(`[parseTemplateForEditNeverOnFirstLoad] 📄   [${idx}] type: ${line.type}, content: "${line.content?.substring(0, 40)}${line.content?.length > 40 ? '...' : ''}", isCustom: ${line.isCustom}, isButtonGroup: ${line.isButtonGroup}, isButton: ${line.isButton}`);
  });
  console.log('[parseTemplateForEditNeverOnFirstLoad] 📄 ===== END =====');
  console.log('==================================================');

  // ============================================================
  // ✅ DEV-ONLY GUARD — 9A (row shape)
  // INVARIANT: Every parsed row has:
  //              • id          (string, non-empty)
  //              • type        (string, non-empty)
  //              • content     (string; empty allowed for `empty` rows)
  //              • depth       (number, >= 0)
  //
  // Reason: parseTemplateForEdit's output flows directly into
  // generateSelectorPathForRow and the editor UI. A row missing any
  // of these fields will:
  //   – render as a blank line in the editor (id missing),
  //   – be mis-classified by generateSelectorPathForRow
  //     (type/depth missing),
  //   – cause depth arithmetic to go negative or NaN.
  //
  // Owned by: this function. The mirrored contract is enforced by
  //           parseTemplateForEditFirstLoadOnly.js.
  // ============================================================
  if (process.env.NODE_ENV !== 'production') {
    const bad = [];
    for (let idx = 0; idx < result.length; idx++) {
      const r = result[idx];
      const problems = [];
      if (!r || typeof r !== 'object') {
        problems.push('row is not an object');
      } else {
        if (!r.id || typeof r.id !== 'string') problems.push('id');
        if (!r.type || typeof r.type !== 'string') problems.push('type');
        if (typeof r.content !== 'string') problems.push('content');
        if (typeof r.depth !== 'number' || Number.isNaN(r.depth))
          problems.push('depth');
        if (typeof r.depth === 'number' && r.depth < 0)
          problems.push('depth<0');
      }
      if (problems.length > 0) {
        bad.push({ index: idx, problems, row: r });
      }
    }
    if (bad.length > 0) {
      console.warn(
        '[INVARIANT:9A] parseTemplateForEdit produced rows with missing or malformed fields.',
        {
          count: bad.length,
          totalRows: result.length,
          samples: bad.slice(0, 5),
        },
      );
    }

    // Two additional cross-row invariants:

    // 1) Every for_loop_start must have a matching for_loop_end
    //    in the same or later row, and vice versa.
    let loopBalance = 0;
    for (const r of result) {
      if (!r || typeof r !== 'object') continue;
      if (r.type === 'for_loop_start') loopBalance++;
      else if (r.type === 'for_loop_end') loopBalance--;
      if (loopBalance < 0) break;
    }
    if (loopBalance !== 0) {
      console.warn(
        '[INVARIANT:9A] parseTemplateForEdit produced unbalanced for_loop_start / for_loop_end.',
        { finalBalance: loopBalance, totalRows: result.length },
      );
    }

    // 2) Every button or button-group row must be flagged with the
    //    corresponding is* boolean. Otherwise downstream consumers
    //    (Controls button extractor, recalculation) won't see them.
    const badButtons = result.filter((r) => {
      if (!r || typeof r !== 'object') return false;
      if (r.type === 'button' && r.isButton !== true) return true;
      if (r.type === 'button-group' && r.isButtonGroup !== true) return true;
      return false;
    });
    if (badButtons.length > 0) {
      console.warn(
        '[INVARIANT:9A] parseTemplateForEdit produced button/button-group rows without the is* flag.',
        {
          count: badButtons.length,
          samples: badButtons.slice(0, 5).map((r) => ({
            id: r.id,
            type: r.type,
            isButton: r.isButton,
            isButtonGroup: r.isButtonGroup,
          })),
        },
      );
    }
  }

  return result;
};